// beforeSend / beforeBreadcrumb for Sentry: masks email addresses and drops
// credentials (Authorization headers, cookies, tokens, secrets) before an
// event or breadcrumb leaves the app.
import { describeError, maskEmails } from './safeErrorLog'

const SENSITIVE_KEY_PATTERN =
  /authorization|cookie|token|secret|password|api[-_]?key/i
const BEARER_PATTERN = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi
const MAX_DEPTH = 10

function isPlainObject(value) {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function scrubValue(value, depth = 0, seen = new WeakSet()) {
  if (typeof value === 'string') {
    return maskEmails(value).replace(BEARER_PATTERN, '$1 [Filtered]')
  }

  if (value === null || typeof value !== 'object') {
    return value
  }

  // Console breadcrumbs keep the logged arguments as they are, so an axios
  // error would otherwise be serialized with its request headers
  if (value instanceof Error) {
    return describeError(value)
  }

  // Leave other class instances (e.g. the Node request Sentry keeps in
  // sdkProcessingMetadata, which is not sent) untouched
  if (!Array.isArray(value) && !isPlainObject(value)) {
    return value
  }

  if (seen.has(value) || depth >= MAX_DEPTH) {
    return '[Filtered]'
  }

  // seen holds only the ancestors, so an object referenced twice is kept
  seen.add(value)

  const scrubbed = Array.isArray(value)
    ? value.map((entry) => scrubValue(entry, depth + 1, seen))
    : Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [
          key,
          typeof entry === 'string' && SENSITIVE_KEY_PATTERN.test(key)
            ? '[Filtered]'
            : scrubValue(entry, depth + 1, seen)
        ])
      )

  seen.delete(value)
  return scrubbed
}

export function scrubSentryEvent(event) {
  return scrubValue(event)
}

export function scrubSentryBreadcrumb(breadcrumb) {
  return scrubValue(breadcrumb)
}
